import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion.js';

/**
 * Observes an element and reports when it has arrived on screen, once.
 *
 * Two arrival rules, because the elements that use this differ in kind:
 *
 *  - A single block ("block") should appear as soon as it is meaningfully
 *    on screen — including at load, when the hero image is already peeking
 *    above the fold. A plain threshold of 0 is what that needs.
 *  - A section container ("section") is a whole viewport tall and drives a
 *    timeline for its children, so a threshold fires either far too late on
 *    desktop or never on a short viewport. It keys off its leading edge
 *    instead, with a bottom inset.
 *
 * Sharing one rule between them is what once hid the hero image on load:
 * the section rule's bottom inset pulled the trigger line above the image's
 * top edge, so it only fired when the visitor scrolled.
 *
 * @param {{ mode?: 'block'|'section', disabled?: boolean }} options
 * @returns {[React.RefObject<HTMLElement>, boolean]} ref and shown state
 */
export function useReveal({ mode = 'block', disabled = false } = {}) {
  const ref = useRef(null);
  const reducedMotion = usePrefersReducedMotion();
  // Always starts "not yet arrived", on the server and on the client alike.
  // Seeding this from the reduced-motion preference instead would make the
  // first client render disagree with the prerendered HTML for any visitor
  // who has that preference set, which is a hydration mismatch. The
  // reduced-motion case is handled two ways that need no seed: the effect
  // below resolves it immediately, and motion.css forces the arrived state
  // under the media query regardless.
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (reducedMotion || disabled) {
      setShown(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        }
      },
      mode === 'section'
        ? { threshold: 0, rootMargin: '0px 0px -18% 0px' }
        : { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [mode, reducedMotion, disabled]);

  return [ref, shown];
}

/** The attribute pair every motion.css rule reads. */
export const revealState = (shown) => (shown ? 'shown' : 'hidden');
